import type moment from 'moment'
import type { $Values } from 'utility-types'

import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import type { ComplaintMessages } from 'modules/complaints/store/collections/complaintMessages'
import complaints from 'modules/complaints/store/collections/complaints'
import type Complaint from 'modules/complaints/store/models/Complaint'
import { STATUS } from 'modules/complaints/store/models/Complaint'
import type ComplaintMessage from 'modules/complaints/store/models/ComplaintMessage'

interface ElementConstructor {
  type: 'message' | 'date'
  dateType?: $Values<typeof STATUS>
  date: moment.Moment
  message: ComplaintMessage | null | undefined
  complaint: Complaint | null | undefined
}

export class Element {
  type: string
  dateType: string | null | undefined
  date: moment.Moment
  message: ComplaintMessage | null | undefined
  complaint: Complaint | null | undefined

  constructor({ type, dateType, date, message = null, complaint = null }: ElementConstructor) {
    this.type = type
    this.dateType = dateType
    this.date = date
    this.message = message
    this.complaint = complaint
  }

  get isDate(): boolean {
    return this.type === 'date'
  }

  get isMessage(): boolean {
    return !!this.message
  }

  get id(): string {
    if (this.isDate) return `date-${this.dateType || ''}`

    return `message-${this.message?.id || ''}`
  }

  get title(): string {
    if (this.isDate) return this.dateTitle || ''

    if (this.message?.isCompanyMessage) {
      return i18n.t('complaints.activity.feed.message_company')
    }

    if (this.complaint?.isAnonymous) {
      return i18n.t('complaints.activity.feed.message_anonymous')
    }

    const complaint = (this.message && complaints.get(this.message.attributes.complaint_id)) ?? null

    return i18n.t('complaints.activity.feed.message_identified', {
      name: complaint?.complainerName,
    })
  }

  get dateTitle(): string | null | undefined {
    switch (this.dateType) {
      case 'created':
        return i18n.t('complaints.activity.feed.created_at')

      case 'acknowledged':
        return i18n.t('complaints.activity.feed.acknowledged_at')

      case 'completed':
        return i18n.t('complaints.activity.feed.completed_at')

      case 'discarded':
        return i18n.t('complaints.activity.feed.discarded_at')

      default:
        return null
    }
  }

  get text() {
    if (!this.isDate) return this.message?.text

    switch (this.dateType) {
      case STATUS.Created:
        return this.complaint?.createdAt

      case STATUS.Acknowledged:
        return this.complaint?.acknowledgedAt

      case STATUS.Completed:
        return this.complaint?.completedAt

      case STATUS.Discarded:
        return this.complaint?.discardedAt

      default:
        return null
    }
  }
}

const useFeed = createComputed(
  (complaintMessages: ComplaintMessages, complaint: Complaint): Element[] => {
    // Removing first message since it's the description
    const messages = complaintMessages.toArray()
    messages.shift()

    const feed = messages.map((message) => {
      return new Element({
        type: 'message',
        date: message.createdAt,
        message,
        complaint: complaints.get(message.attributes.complaint_id),
      })
    })

    if (complaint.createdAt) {
      feed.push(
        new Element({
          type: 'date',
          dateType: STATUS.Created,
          date: complaint.createdAt,
          message: null,
          complaint,
        })
      )
    }

    if (complaint.acknowledgedAt) {
      feed.push(
        new Element({
          type: 'date',
          dateType: STATUS.Acknowledged,
          date: complaint.acknowledgedAt,
          message: null,
          complaint,
        })
      )
    }

    if (complaint.completedAt) {
      feed.push(
        new Element({
          type: 'date',
          dateType: STATUS.Completed,
          date: complaint.completedAt,
          message: null,
          complaint,
        })
      )
    }

    if (complaint.discardedAt) {
      feed.push(
        new Element({
          type: 'date',
          dateType: STATUS.Discarded,
          date: complaint.discardedAt,
          message: null,
          complaint,
        })
      )
    }

    return feed.sort((a, b) => (a.date.isBefore(b.date) ? 1 : -1))
  }
)

export default useFeed
